import React, { useContext, useState } from 'react';

import { Answer, Question } from '~/types';

import styled from 'styled-components';
import Fade from '~/components/Fade';
import { useTranslation } from 'react-i18next';
import { AppContext } from '~/contexts/AppContext';
import { QuestionContext } from '~/contexts/QuestionContext';
import SendButton from '~/components/SendButton';

import MatomoTracker from '~/util/matomo/MatomoTracker';

import axios from 'axios';

import * as topicUtils from '~/util/topic';
import { useParams } from 'react-router-dom';

interface CheckboxRowProps {
    isSelected: boolean;
}

const CheckboxRow = styled.div<CheckboxRowProps>`
    display: block;
    color: white;
    vertical-align: top;
    margin-bottom: 13px;

    ${props => (
        props.isSelected ? `
            background:linear-gradient(90deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.25) 100%);
            border-radius: 4px;
        ` : ""
    )}
    
    &:hover {
        cursor: pointer;
    }

    input[type='checkbox'] {
        display: none;
    }
    
    input[type='checkbox']:disabled + label {
        color: #A6A6A6;
    }

    input[type='checkbox']:disabled + label::before {
        border-radius: 3.3888px;
        border: 1.37px solid #b3b3b3;
        background: #fff4;
        font-size: 1rem;
        box-shadow: 0px 0px 0px
    }

    input[type='checkbox'] + label {
        cursor: pointer;
        vertical-align: top;
        display: block;
        font-size: 1rem;
        padding: 13px;
        position: relative;
    }

    input[type='checkbox'] + label::before {
        margin-right:14px;
        content: '';
        width: 30px;
        height: 30px;
        display: inline-block;
        border-radius: 3.3888px;
        border: 1.37px solid #FFF;
        background: #ffffff0d;
        vertical-align: top;
    }
    
    input[type='checkbox']:checked + label::before {
        border-radius: 3.3888px;
        border: 1.37px solid #FFF;
        background: #49596B;
        font-size: 1rem;
        box-shadow: 0px 0px 0px 3.441px rgba(255, 255, 255, 0.15);
    }

    .checkbox-text {
        display: inline-block;
        width: calc(100% - 50px);
        padding-top: 5px;
    }

    .checkmark-container {
        position: absolute;
        top: 13px;
        left: 13px;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
    }
`;

const MultipleChoiceAnswer: React.FC = () => {

    const { topicData, apiUrl, preciseCoordinates } = useContext(AppContext)!;
    const { ratingId, question, questionTime, onSubmitAnswer } = useContext(QuestionContext)!;
    
    const [selectedList, setSelectedList] = useState<{ [answerId: number]: boolean }>({});

    const params: { topicHash: string } = useParams();

    const { t, i18n } = useTranslation();

    const selectAnswer = (answerId: number) => {
        if(selectedList[answerId]) {
            delete selectedList[answerId];
        } else {
            selectedList[answerId] = true;
        }
        setSelectedList({ ...selectedList });
    };

    const buttonClicked = async () => {

        const answerMcIds = Object.keys(selectedList).map(Number);

        await axios.post(`${apiUrl}/rating-answer/${topicData?.topic.id}/multiple-choice`, {
            ratingId: ratingId,
            questionnaireId: question.questionnaireId,
            questionId: question.questionId,
            preciseCoordinates: preciseCoordinates,
            timeToAnswerMs: !questionTime ? 0 : Date.now() - questionTime,
            lang: i18n.language,
            answerMcIds
        });

        MatomoTracker.trackEvent({
            category: 'qna', 
            action: 'submit-answer',
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${params.topicHash}`,
            customDimensions: [
                { id: 3, value: question.questionText },
                { id: 4, value:
                    answerMcIds.map(id =>
                        topicUtils.getAnswerText(topicData, question.questionId, id, i18n.language)
                    ).join('/%/')
                },
                { id: 5, value: '' }
            ]
        });

        onSubmitAnswer(null);
    };

    const answerLimit = question.answerLimit ?? 0;
    const answerLimitReached = answerLimit > 1 && Object.keys(selectedList).length >= answerLimit;
    const atLeastOneAnswerSelected = Object.keys(selectedList).length > 0;

    return (
        <Fade cascade>
            <div style={{ background: '#00000055', marginBottom: '20px', padding: '13px', borderRadius: '10px' }}>
                <Fade cascade>
                    {question.answers.map((answer: Answer) =>
                        <CheckboxRow key={answer.answerId} isSelected={selectedList[answer.answerId]}>
                            <input
                                type="checkbox"
                                id={answer.answerId.toString()}
                                name={answer.answerId.toString()}
                                value={answer.answerText}
                                onClick={() => selectAnswer(answer.answerId)}
                                disabled={!selectedList[answer.answerId] && answerLimitReached}
                            />
                            <label htmlFor={answer.answerId.toString()}>
                                {selectedList[answer.answerId] && (
                                    <div className="checkmark-container">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="15" viewBox="0 0 12 9" fill="none">
                                            <path
                                                d="M10.5 1.625L4.3125 7.8125L1.5 5"
                                                stroke="white"
                                                strokeWidth="1.71429"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                )}
                                <div className='checkbox-text'>{topicUtils.getAnswerText(
                                    topicData,
                                    question.questionId,
                                    answer.answerId,
                                    i18n.language
                                )}</div>
                            </label>
                        </CheckboxRow>
                    )}
                </Fade>
            </div>
            <Fade>
                <SendButton disabled={!atLeastOneAnswerSelected} onClick={buttonClicked} />
            </Fade>
        </Fade>
    );
};

export default MultipleChoiceAnswer;